import { logger } from "@/lib/utils/logger";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3005";

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params?: Record<string, any>;
  isFormData?: boolean;
}

interface ApiError {
  message: string;
  statusCode: number;
  error?: string;
  code?: string;
  [key: string]: unknown;
}

// Stable codes the API sends from auth guards. We match on these (not on
// the human-readable message) so the centralized logout keeps working even
// if message text changes.
const PHARMACY_EXPIRED_CODE = "PHARMACY_NOT_AUTHENTICATED";
const ACTIVE_USER_EXPIRED_CODES = new Set([
  "NO_ACTIVE_USER",
  "INVALID_ACTIVE_USER",
]);

// Fallback string match for older API deployments that don't send codes yet.
const PHARMACY_EXPIRED_MESSAGE = "Not authenticated";
const ACTIVE_USER_EXPIRED_MESSAGES = new Set([
  "No active user selected",
  "Invalid active user session",
]);

export class ApiClientError extends Error {
  statusCode: number;
  error?: string;
  data: Record<string, unknown>;

  constructor(data: ApiError) {
    // For permission-denied errors, always show a user-friendly message
    // instead of raw API strings like "Role 'x' does not have permission 'y'"
    const message =
      data.code === "PERMISSION_DENIED"
        ? "You don't have permission to perform this action."
        : data.message;
    super(message);
    this.name = "ApiClientError";
    this.statusCode = data.statusCode;
    this.error = data.error;
    this.data = data;
  }
}

let refreshPromise: Promise<boolean> | null = null;
let isRedirecting = false;

async function refreshTokens(): Promise<boolean> {
  try {
    const res = await fetch(`${API_URL}/auth/refresh`, {
      method: "POST",
      credentials: "include",
    });
    return res.ok;
  } catch {
    return false;
  }
}

async function handleTokenRefresh(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = refreshTokens().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

export async function apiClient<T = unknown>(
  path: string,
  options: RequestOptions = {}
): Promise<T> {
  const { body, params, isFormData, headers: customHeaders, ...rest } = options;

  let url = `${API_URL}${path}`;
  if (params) {
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) {
        searchParams.set(key, String(value));
      }
    }
    const qs = searchParams.toString();
    if (qs) url += `?${qs}`;
  }

  const headers: Record<string, string> = {
    ...(customHeaders as Record<string, string>),
  };

  if (body !== undefined && !isFormData) {
    headers["Content-Type"] = "application/json";
  }

  const config: RequestInit = {
    credentials: "include",
    headers,
    ...rest,
  };

  if (body !== undefined) {
    config.body = isFormData ? (body as BodyInit) : JSON.stringify(body);
  }

  let response = await fetch(url, config);

  if (
    response.status === 401 &&
    !path.includes("/auth/refresh") &&
    !path.includes("/auth/login")
  ) {
    const refreshed = await handleTokenRefresh();
    if (refreshed) {
      response = await fetch(url, config);
    }

    // If still 401 after refresh attempt, check whether it's a true session
    // expiry (auth guard rejection) vs a business-logic 401 (e.g. invalid password).
    // Only redirect for guard-level failures; let everything else propagate normally.
    // Exclude /auth/current-user so the auth provider can handle its own error path.
    if (response.status === 401 && !path.includes("/auth/current-user")) {
      let errorCode = "";
      let errorText = "";
      try {
        const body = await response.clone().json();
        // Server's global exception filter writes errors as { error, code, ... }
        // (NOT { message, ... }), so read both fields.
        errorCode = (body?.code as string) ?? "";
        errorText = (body?.error as string) ?? (body?.message as string) ?? "";
      } catch {
        // Can't parse — fall through to normal error handling
      }

      const isPharmacyExpired =
        errorCode === PHARMACY_EXPIRED_CODE ||
        errorText === PHARMACY_EXPIRED_MESSAGE;
      const isActiveUserExpired =
        ACTIVE_USER_EXPIRED_CODES.has(errorCode) ||
        ACTIVE_USER_EXPIRED_MESSAGES.has(errorText);

      if ((isPharmacyExpired || isActiveUserExpired) && !isRedirecting) {
        isRedirecting = true;
        const redirectTo = isPharmacyExpired ? "/login" : "/select-user";

        // Breadcrumb so we can see in Sentry which guard fired and on what
        // path — useful for debugging premature session loss reports.
        logger.breadcrumb("auth", "session-expired auto-redirect", {
          path,
          status: response.status,
          code: errorCode || undefined,
          error: errorText || undefined,
          redirectTo,
        });

        sessionStorage.removeItem("activeUser");
        window.location.replace(redirectTo);
        return new Promise<never>(() => {});
      }
    }
  }

  if (!response.ok) {
    // If a redirect to /login or /select-user is already in progress,
    // suppress all errors to prevent flash of error UI during navigation
    if (isRedirecting) {
      return new Promise<never>(() => {});
    }

    let errorData: ApiError;
    try {
      errorData = await response.json();
      // Always set statusCode and message from HTTP response if not in body
      if (!errorData.statusCode) {
        errorData.statusCode = response.status;
      }
      if (!errorData.message) {
        errorData.message =
          errorData.error || response.statusText || "Request failed";
      }
    } catch {
      errorData = {
        message: response.statusText || "Request failed",
        statusCode: response.status,
      };
    }
    throw new ApiClientError(errorData);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

export const api = {
  get: <T = unknown>(path: string, options?: RequestOptions) =>
    apiClient<T>(path, { ...options, method: "GET" }),

  post: <T = unknown>(path: string, body?: unknown, options?: RequestOptions) =>
    apiClient<T>(path, { ...options, method: "POST", body }),

  put: <T = unknown>(path: string, body?: unknown, options?: RequestOptions) =>
    apiClient<T>(path, { ...options, method: "PUT", body }),

  patch: <T = unknown>(
    path: string,
    body?: unknown,
    options?: RequestOptions
  ) => apiClient<T>(path, { ...options, method: "PATCH", body }),

  delete: <T = unknown>(
    path: string,
    options?: RequestOptions & { body?: unknown }
  ) => apiClient<T>(path, { ...options, method: "DELETE" }),
};
